import { SidebarProvider } from '@ntconsult/aluma-react';
import { useUIStore } from '@store/uiStore';
import { useSyncExternalStore } from 'react';
import { Outlet } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { AppSidebar } from './AppSidebar';
import { PageBreadcrumb } from './PageBreadcrumb';

/** Mesmo corte do `mobileBreakpoint` default do `SidebarProvider` (768px). */
const MOBILE_QUERY = '(max-width: 767px)';

function subscribeToMobile(onChange: () => void) {
  const media = window.matchMedia(MOBILE_QUERY);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

function useIsMobile() {
  return useSyncExternalStore(subscribeToMobile, () => window.matchMedia(MOBILE_QUERY).matches);
}

/**
 * Publica a largura visível do `<main>` em `--main-width`: com uma tabela mais
 * larga que a página, o `<main>` rola na horizontal e os blocos em volta dela
 * ficam parados com essa largura (`PINNED_BLOCK`, em `wideTable.ts`). Ref
 * callback com limpeza (React 19), sem efeito; `contentRect` já desconta a
 * barra de rolagem.
 */
function publishVisibleWidth(main: HTMLElement) {
  const observer = new ResizeObserver(([entry]) => {
    main.style.setProperty('--main-width', `${entry.contentRect.width}px`);
  });
  observer.observe(main);
  return () => observer.disconnect();
}

export function Layout() {
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);
  const isMobile = useIsMobile();

  return (
    // `icon` no desktop: só nele o Aluma esconde rótulos e abre submenus como menu
    // flutuante (com `offcanvas` o submenu sai cortado). No celular, a gaveta.
    <SidebarProvider
      open={sidebarOpen}
      onOpenChange={setSidebarOpen}
      collapsible={isMobile ? 'offcanvas' : 'icon'}
    >
      <div className="flex h-dvh w-full">
        <AppSidebar />
        {/* min-w-0: sem isso, um filho largo (ex.: tabela com colunas min-w-*)
            empurra esta coluna (e a linha flex inteira) além da viewport em
            vez de deixar o overflow-x-auto interno da tabela absorver. */}
        <div className="flex min-w-0 flex-1 flex-col">
          <AppHeader />
          {/* Fora do <main>: com tabela larga ele rola na horizontal e a trilha iria junto. */}
          <div className="px-6 pt-6">
            <PageBreadcrumb />
          </div>
          {/* Única área de rolagem do app.
              - `relative`: sem isso, filhos `absolute` sem ancestral posicionado (ex.: o
                `sr-only` "Mais páginas" da `Pagination`) se ancoram no <body>, escapam da
                rolagem e esticam o documento — uma segunda barra, na janela.
              - padding num wrapper, não no <main>: cabeçalho `sticky` (tabela) respeita o
                padding do container que rola e grudaria 24px abaixo do topo, com as
                linhas aparecendo por cima dele. */}
          <main
            ref={publishVisibleWidth}
            className="relative min-h-0 min-w-0 flex-1 overflow-y-auto"
          >
            {/* pt-4: o mesmo `gap-4` entre trilha e título do slot `breadcrumb` do `PageHeader`. */}
            <div className="px-6 pt-4 pb-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
